import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { Select } from "../Select";

import classes from "./Tabs.scss";

/**
 * A component for rendering tabs.
 *
 * - Make sure to import both the `Tabs` and `Tab` components.
 * - State is controlled by the parent component and it works similar to a `select` element.
 *   - `value` is used to determine which `Tab` should be active.
 *   - `onChange` is used when a `Tab` is clicked.
 * - The following 2 `Tab` props are considered _private_ and are passed by the parent `Tabs` component:
 *   - `active` - set to `true` when the value of the `Tab` matches the value of the `Tabs`
 *   - `onClick` - calls a handler within `Tabs` to call it's `onChange` prop
 * - ARIA attributes are set to make the Tabs more accessible based on guidelines set in [W3C's WAI-ARIA Authoring Practices: Section 3.21 Tabs](https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel).
 *
 * @example
 * <Tabs>
 *   <Tab value="general">General</Tab>
 *   <Tab value="settings">Settings</Tab>
 * </Tabs>
 */
export function Tabs<T extends string = string>({
  children,
  value,
  className,
  responsiveDropdown = true,
  responsiveDropdownClassName,
  style = "default",
  embeddedInCard = false,
  onChange,
}: React.PropsWithChildren<TabsProps<T>>) {
  const handleTabClick = (tabValue: T) => {
    onChange?.(tabValue);
  };

  const handleSelectChange = ({ value }: { value: T }) => {
    onChange?.(value);
  };

  const tabClasses = cx(className, {
    [classes.tabs]: style === "compact" || style === "default",
    [classes["tabs-filled"]]: style === "filled",
    [classes.enableResponsiveSelect]: responsiveDropdown,
  });

  const selectClasses = cx(className, {
    [classes.tabs_select]: true,
    [classes.embeddedInCard]: embeddedInCard,
  });

  const selectTriggerClasses = cx(
    className,
    classes.tabs_select_trigger,
    responsiveDropdownClassName
  );

  const selectMenuListClasses = cx(className, classes.tabs_select_menu);

  const tabs = React.Children.map(children, (child) => {
    // istanbul ignore else - type guard, noop
    if (React.isValidElement(child)) {
      return React.cloneElement<any>(child, {
        onClick: handleTabClick,
        active: value === child.props.value,
        style,
      });
    }
  });

  const selectOptions = React.Children.map(children, (child) => {
    // istanbul ignore else - type guard, noop
    if (React.isValidElement(child)) {
      const { children: label, value, disabled } = child.props;
      return {
        label,
        value,
        disabled,
      };
    }
  });

  return (
    <>
      <div className={tabClasses} role="tablist" aria-orientation="horizontal">
        {tabs}
      </div>
      {responsiveDropdown && (
        <div className={selectClasses} role="tablist">
          <Select
            data-testid="tabs-select"
            options={coalesce(selectOptions, [])}
            onChange={handleSelectChange}
            selectedOptionValue={value}
            triggerClassName={selectTriggerClasses}
            menuClassName={selectMenuListClasses}
          />
        </div>
      )}
    </>
  );
}

export interface TabsProps<T extends string = string> {
  /**
   * Function to notify of tab click
   */
  onChange?: (value: T) => void;

  /**
   * Current value to set Tab to active
   */
  value?: T;

  /**
   * Class name to be added to tabs div
   */
  className?: string;

  /**
   * Sets whether to show a dropdown on low resolutions (less than 768px). `True` by default
   */
  responsiveDropdown?: boolean;

  /**
   * Class name to be added to responsive dropdown (similar to Select `triggerClassName` prop)
   */
  responsiveDropdownClassName?: string;

  /**
   * Sets the margin of the responsive dropdown to work inside a card
   */
  embeddedInCard?: boolean;

  /**
   * Style of the tab. Default is "default"
   */
  style?: "default" | "compact" | "filled";
}
